import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import type { Expression } from 'mapbox-gl';
import { ltdPaint, ltdPointPaint } from './ltd-slot-layer';
import { ltdSlotsTileUrl } from './utils';

/**
 * Stable module ref for "no sticks to draw" — an empty `in` matches nothing, and a shared
 * identity keeps react-map-gl from re-applying the filter (which janks the camera).
 */
const NO_SLOTS: Expression = ['in', 'insights_unit_formation_slot_id'];

interface EvalModalLtdLayerProps {
  /** The package whose scenario tiles are served. */
  packageId: number;
  /** The eval's effective Type Curve scenario; null draws nothing. */
  scenarioId: number | null;
  /** The IUFS row ids to draw — the selected row's slots, else the union across the visible rows. */
  slotIds: readonly number[];
}

/**
 * The LTD sticks over the Manage Type Curves modal's area preview.
 *
 * The tile is the SCENARIO form (`ltdSlotsTileUrl`), a cheap superset of every promoted version in
 * the scenario, and this layer narrows it to the ids the row resolved. That split is deliberate and
 * documented on both sides: the token-baking source would mint a filterSet per override set, so
 * trying five packages in the modal would resolve five times and write thousands of member rows
 * each (see `Package::Maps::Tiles::InsightsLTDSlots`). It also means the ids are the ONLY thing
 * keeping another version's sticks off the map — a formation filter would draw the same composite
 * slot from a curve nobody picked — so they come from the same aggregate as the row's LTD count
 * (`InventoryWorkspace::LaunchContext::LtdDetail`), and the number and the picture cannot disagree.
 *
 * Paints are shared with the page map's own LTD layer, so a planned location reads identically in
 * both places.
 *
 * Deliberately propless of atoms: the modal is the TC team's shared component, so everything this
 * needs arrives as props and nothing here reaches into the eval page's state.
 *
 * @param {EvalModalLtdLayerProps} props - The package, the scenario, and the slots to draw.
 * @return {JSX.Element | null} The sticks source with its line + point layers.
 */
export function EvalModalLtdLayer({
  packageId,
  scenarioId,
  slotIds,
}: EvalModalLtdLayerProps) {
  // Memoized on the id list so a fresh filter identity — which re-applies the filter mid-zoom —
  // is produced only when the drawn set actually changes.
  const filter = useMemo<Expression>(() => {
    if (!slotIds.length) return NO_SLOTS;
    return ['in', 'insights_unit_formation_slot_id', ...slotIds];
  }, [slotIds]);

  if (scenarioId === null) return null;

  return (
    <Source
      id="eval-tc-modal-ltd"
      type="vector"
      tiles={[ltdSlotsTileUrl(packageId, scenarioId)]}
    >
      {/* The tile decides which of these is populated by zoom (DETAIL_MIN_ZOOM): sticks at
          detail zoom, the surface point below it. Both mount unconditionally — an empty
          source-layer draws nothing — so the source stays stable as the camera moves. */}
      <Layer
        id="eval-tc-modal-ltd-stick"
        type="line"
        source-layer="ltd-stick"
        filter={filter}
        paint={ltdPaint}
      />
      <Layer
        id="eval-tc-modal-ltd-point"
        type="circle"
        source-layer="ltd-point"
        filter={filter}
        paint={ltdPointPaint}
      />
    </Source>
  );
}
